import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import moment from "moment";
import { createStyledIndicatorButton } from "../../../common/StyledComponents/SideBarComponents";
import filesvg from "../../../assets/images/chats/file_svg.svg";
import { SmallIcon } from "../../../common/chatComponents/ScreenSearchSection";
import editTopics from "../../../assets/images/editTopic.svg";
import download from "../../../assets/images/downloadLogo.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  FILE_CATEGORY,
  getAttachmentCategory,
  getFileIconUrl,
  isGoogleYoutubeUrl,
} from "../../../utils/findextension";
import InputDialogComponent from "../../../components/InputDialog";
import {
  getAttachments,
  handleEditFileName,
  handleMediaDownload,
} from "../../../appStore/actions/ArkchatGroup/action";
import session from "../../../utils/helper";
import searchIcon from "../../../assets/images/chats/searchIcon.svg";
import useAppSelectors from "../../../customHooks/useAppSelectors";

import { updateAttachmentNameByLocation } from "../../../config/db";
const classes = {
  drawerBody: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },
  header: {
    backgroundColor: "#183243",
    color: "#0000",
    padding: "0 0.6rem",
    minHeight: "38px",
  },
  headerIconBox: {
    backgroundColor: "transparent",
    position: "relative",
    height: "26px",
    width: "26px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "2px",
    border: "1px solid #FFFFFF",
    flexShrink: 0,
  },
  headerIcon: {
    color: "#FFFFFF",
    height: "100%",
    width: "100%",
  },
  searchIconWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  categoryPill: {
    cursor: "pointer",
    color: "#000000",
    backgroundColor: "#FFFFFF",
    border: "1px solid #000000",
    borderRadius: "4px",
    padding: "0.25rem 0.7rem",
    fontSize: "0.8rem",
    fontWeight: 400,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  categoryPillActive: {
    cursor: "pointer",
    color: "#FFFFFF",
    backgroundColor: "#183243",
    border: "1px solid #183243",
    borderRadius: "4px",
    padding: "0.25rem 0.7rem",
    fontSize: "0.8rem",
    fontWeight: 400,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
};

const CATEGORY_TABS = [
  { value: FILE_CATEGORY.ALL, label: "All" },
  { value: FILE_CATEGORY.DOCS, label: "Files" },
  { value: FILE_CATEGORY.MEDIA, label: "Media" },
  { value: FILE_CATEGORY.LINKS, label: "Links" },
  { value: FILE_CATEGORY.DRIVE, label: "Drive" },
];

// Same "# {number} - {heading}" convention as Threads.jsx's getThreadLabel,
// except here we return null (render nothing) when the attachment isn't
// part of any thread at all, rather than falling back to a bare "# Thread".
const getFileThreadLabel = (item) => {
  if (!item?.thread_number) {
    return null;
  }
  return item?.thread_heading
    ? `# ${item.thread_number} - ${item.thread_heading}`
    : `# ${item.thread_number} Thread`;
};

const MediaFiles = ({
  setMediaFilesOpen,
  isMediaFilesOpen,
  handlegetThreadMessages,
  onPreviewFile,
}) => {
  const EditButton = createStyledIndicatorButton("#E3E1E1");
  const [isEditFileOpen, setEditFileOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [oldFileName, setOldFileName] = useState("");
  const [newFileName, setNewFileName] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [activeCategory, setActiveCategory] = useState(FILE_CATEGORY.ALL);
  const { currentUserData } = useAppSelectors();
  const selectedGroup = useSelector(
    (state) => state?.appStateData?.selectedGroupData,
  );
  const selectedTopic = useSelector(
    (state) => state?.appStateData?.selectedTopicData,
  );
  const threadMsgList = useSelector(
    (state) => state?.arkchatgroup?.threadMsgList,
  );
  const activeThreadId = threadMsgList?.[0]?.id;
  const isThreadScreen = session.get("isThreadScreen");

  const dispatch = useDispatch();

  let fileListData = useSelector(
    (state) => state?.arkchatgroup?.attachmentData,
  );

  const filteredAttachments = useMemo(() => {
    const attachments = fileListData?.attachments || [];
    if (activeCategory === FILE_CATEGORY.ALL) {
      return attachments;
    }
    return attachments.filter(
      (item) => getAttachmentCategory(item) === activeCategory,
    );
  }, [fileListData?.attachments, activeCategory]);

  const handleGetFiles = () => {
    if (selectedGroup?.isGroup) {
      if (!isThreadScreen) {
        if (selectedTopic) {
          dispatch(
            getAttachments(
              `${selectedGroup?.pnId}?topicId=${selectedTopic?.id}`,
            ),
          );
        } else {
          dispatch(getAttachments(`${selectedGroup?.pnId}`));
        }
      } else {
        dispatch(
          getAttachments(
            `${selectedGroup?.pnId}?threadId=${threadMsgList[0]?.id}`,
          ),
        );
      }
    } else {
      dispatch(getAttachments(`${selectedGroup?.pnId}`));
    }
  };

  useEffect(() => {
    // MediaFiles stays mounted inside a persistent Drawer across chat
    // switches, so refetch whenever it's opened or the selected
    // group/topic/thread changes instead of only once on mount.
    if (isMediaFilesOpen && selectedGroup?.pnId) {
      handleGetFiles();
    }
  }, [isMediaFilesOpen, selectedGroup?.pnId, selectedTopic?.id, activeThreadId]);

  // Someone renamed a file in this chat (attachment-renamed signal).
  const attachmentsChange = useSelector(
    (state) => state?.localAppReducer?.attachmentsChange,
  );
  useEffect(() => {
    if (isMediaFilesOpen && attachmentsChange?.pnId && attachmentsChange.pnId === selectedGroup?.pnId) {
      handleGetFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentsChange?.seq]);

  const handleSaveNewName = (id, item) => {
    const reqBody = {
      msg_id: id,
      old_name: oldFileName,
      new_name: newFileName,
    };
    dispatch(handleEditFileName(reqBody, id)).then((res) => {
      if (res?.status) {
        handleGetFiles();
        setEditFileOpen(false);
        updateAttachmentNameByLocation(id, item?.location, newFileName);
      }
    });
    setEditFileOpen(false);
    setSelectedFileId(null);
    setOldFileName("");
    setNewFileName("");
  };

  const handleFileDownload = (fileData, action="") => {
    if (!isGoogleYoutubeUrl(fileData?.location)) {
      const reqBody = {
        location: fileData?.location,
        action,
      };

      dispatch(handleMediaDownload(reqBody)).then((dataUrl) => {
        if (dataUrl&&action!=="") {
          const url = window.URL.createObjectURL(dataUrl);

          const a = document.createElement("a");
          a.href = url;
          a.download = fileData?.name || "download";

          document.body.appendChild(a);
          a.click();
          a.remove();

          window.URL.revokeObjectURL(url);
        }else{
          window.open(dataUrl?.data, "_blank");
        }
      });
    } else {
      window.open(fileData?.location, "_blank");
    }
  };

  return (
    <Box sx={classes?.drawerBody}>
      <Stack sx={{ height: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ ...classes.header, flexShrink: 0 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.2}
            sx={{ cursor: "pointer", minWidth: 0 }}
            onClick={() => setMediaFilesOpen(false)}
          >
            <Box sx={classes.headerIconBox}>
              <CloseIcon sx={classes.headerIcon} />
            </Box>
            <Typography color={"white"} fontSize={"14px"} fontWeight={400} noWrap>
              Group Files & Links
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Box sx={classes.searchIconWrap}>
              <SmallIcon
                src={searchIcon}
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </Box>
            {/* <Box
              sx={classes.headerIconBox}
              onClick={() => setEditMode(!editMode)}
            >
              <SmallIcon
                src={editTopics}
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </Box> */}
          </Stack>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ overflowX: "auto", flexShrink: 0 }}
          mx={"0.7rem"}
          mt={"0.7rem"}
          mb={"1rem"}
        >
          {CATEGORY_TABS.map(({ value, label }) => (
            <Box
              key={value}
              onClick={() => setActiveCategory(value)}
              sx={
                activeCategory === value
                  ? classes.categoryPillActive
                  : classes.categoryPill
              }
            >
              {label}
            </Box>
          ))}
        </Stack>
        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "3px",
            },
          }}
          mx={"0.7rem"}
          mb={"0.7rem"}
        >
          <>
            {filteredAttachments?.length > 0 &&
              filteredAttachments?.map((item) => (
                <Stack
                  mb={"0.4rem"}
                  direction={"row"}
                  alignItems={"center"}
                  spacing={1}
                  sx={{ cursor: "pointer" }}
                  key={item?.id + "attachmets" + item?.name}
                >
                  {/* <Box width={"21%"}>
                                <Typography>
                                  {moment(data?.createdAt).format(
                                    userData?.profileData?.data?.dateFormat
                                  )}
                                </Typography>
                              </Box> */}
                  <Stack
                    width={"100%"}
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    padding={"0.2rem 0rem"}
                    mb={"8px"}
                  >
                    <Box
                      sx={{ flex: 1, minWidth: 0 }}
                      display={"flex"}
                      alignItems={"center"}
                      gap={"0.4rem"}
                      onClick={() => handleFileDownload(item)}
                    >
                      <Box display={"flex"} alignItems={"center"}>
                        <img
                          style={{ height: "28px", width: "28px" }}
                          src={getFileIconUrl(item?.location)}
                          alt="logo"
                        />
                      </Box>
                      <Box
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        <Typography
                          variant="h5"
                          fontSize={"0.8rem"}
                          fontWeight={400}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={item?.name}
                        >
                          {item?.name}
                        </Typography>
                        {item?.from?.name && (
                          <Typography
                            fontSize={"0.68rem"}
                            color={"#8C8C8C"}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {`Shared by: ${item.from.name}${
                              item?.timetoken
                                ? `, ${moment(
                                    Math.floor(item.timetoken / 10000),
                                  ).format(
                                    currentUserData?.userDateTimeFormat ||
                                      "DD MMM YYYY",
                                  )}`
                                : ""
                            }`}
                          </Typography>
                        )}
                        {getFileThreadLabel(item) && (
                          <Typography
                            fontSize={"0.68rem"}
                            color={"#183243"}
                            fontWeight={500}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: handlegetThreadMessages
                                ? "pointer"
                                : "default",
                              "&:hover": handlegetThreadMessages
                                ? { textDecoration: "underline" }
                                : undefined,
                            }}
                            title="Go to thread"
                            onClick={(e) => {
                              if (!handlegetThreadMessages) return;
                              e.stopPropagation();
                              setMediaFilesOpen(false);
                              handlegetThreadMessages(
                                { id: item?.id, thread_id: item?.thread_id },
                                false,
                              );
                            }}
                          >
                            {getFileThreadLabel(item)}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {onPreviewFile && (
                        <EditButton
                          onClick={() => onPreviewFile(item)}
                          sx={{
                            bgcolor: "transparent",
                            color: "#000000",
                          }}
                          size="small"
                          variant="contained"
                          title="Preview"
                        >
                          <VisibilityOutlinedIcon sx={{ fontSize: "1.1rem" }} />
                        </EditButton>
                      )}
                      {isEditFileOpen && selectedFileId === item?.id && (
                        <InputDialogComponent
                          title={"Edit File Name"}
                          isType={"editFileName"}
                          btnLabel="Save"
                          value={newFileName}
                          onChange={(e) => setNewFileName(e.target.value)}
                          open={isEditFileOpen}
                          handleClose={() => {
                            setEditFileOpen(false);
                            setSelectedFileId(null);
                            setOldFileName("");
                            setNewFileName("");
                          }}
                          onClick={() => handleSaveNewName(item?.id, item)}
                        />
                      )}
                      {editMode ? (
                        <EditButton
                          onClick={() => {
                            setEditFileOpen(!isEditFileOpen);
                            setSelectedFileId(item?.id);
                            setOldFileName(item?.name);
                            setNewFileName(item?.name);
                          }}
                          sx={{
                            border: "1px solid black",
                            bgcolor: "transparent",
                          }}
                          size="small"
                          variant="contained"
                        >
                          <SmallIcon src={editTopics} />
                        </EditButton>
                      ) : (
                        <EditButton
                          onClick={() => handleFileDownload(item,"download")}
                          sx={{
                            bgcolor: "transparent",
                          }}
                          size="small"
                          variant="contained"
                        >
                          <SmallIcon src={download} />
                        </EditButton>
                      )}
                    </Box>
                  </Stack>
                </Stack>
              ))}
          </>

          {filteredAttachments?.length === 0 && (
            <Box mt={"48%"}>
              <Box width={"25%"} margin={"auto"} mb={"0.7rem"}>
                <img style={{ width: "100%" }} src={filesvg} alt="icon" />
              </Box>
              <Box textAlign={"center"}>
                <Typography variant="h6" fontSize={"1rem"}>
                  {activeCategory === FILE_CATEGORY.ALL
                    ? "There are no files in this group's Topic"
                    : `No ${
                        CATEGORY_TABS.find((tab) => tab.value === activeCategory)
                          ?.label
                      } found`}
                </Typography>
              </Box>
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default MediaFiles;
